import { useField } from 'formik'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectSearchContext } from '../../lib/Context'

interface Props {
  children: React.ReactNode
  name: string
  className?: string
}

const SelectSearch: FC<Props> = ({ children, name, className }) => {
  const { t } = useTranslation('common')
  const [item, setItem] = useState<{ name: string; value: string }>()
  const [isActive, setActive] = useState<boolean>(false)
  const [searchWord, setSearchWord] = useState<string>('')
  const [field, meta, helpers] = useField({ name })

  // Listen for field reset, when form is submitted
  useEffect(() => {
    if (!field.value && !isActive) {
      setItem(undefined)
      setSearchWord('')
    }
  }, [field])

  useEffect(() => {
    if (item && item.value) {
      setActive(false)
      setSearchWord(item.name)
      helpers.setValue(item.value)
    }
  }, [item])

  return (
    <div className={`relative ${className}`}>
      <SelectSearchContext.Provider value={{ setItem, searchWord }}>
        <input
          type='text'
          className={`flex-1 appearance-none w-full py-2 px-4  bg-white text-gray-700 placeholder-gray-400 text-base focus:outline-0 outline-0 ${isActive ? 'rounded-t-md border-t border-x' : 'rounded-md border'}  border-gray-300 ${isActive ? 'mb-0' : ''}`}
          placeholder={t('common:selectSearch.placeholder')}
          value={searchWord}
          onChange={(event) => {
            setSearchWord(event.currentTarget.value)

            // If an item is already set when editing the input, this means we are looking for another item, so reset
            if (item) {
              setItem(undefined)
              helpers.setValue(undefined)
            }
          }}
          onFocus={() => setActive(true)}
          onBlur={() => {
            setActive(false)
            helpers.setTouched(true)
          }}
        />
        {isActive && (
          <div className='absolute w-full bg-white z-100 text-gray-700 placeholder-gray-400 text-base rounded-b-md border border-gray-300 h-24 overflow-scroll'>
            {children}
          </div>
        )}
        {meta.touched && meta.error
          ? (
            <div className='absolute -bottom-2 right-2 bg-white px-2 text-xs text-red-400'>
              {meta.error}
            </div>
          )
          : null}
      </SelectSearchContext.Provider>
    </div>
  )
}

export const SelectSearchItem = ({
  value,
  children,
}: {
  value: string
  children: string
}) => {
  const { setItem, searchWord } = useContext(SelectSearchContext)

  if (children.toLowerCase().indexOf(searchWord.toLowerCase()) === -1) {
    return null
  }

  return (
    <div
      className='cursor-pointer px-4 py-1 border-b'
      onMouseDown={() => setItem({ name: children, value })}
    >
      {children}
    </div>
  )
}

export default SelectSearch
